import { login } from 'Api/ProfileApi';
import { DeviceRegistrationModal } from 'Features/DeviceRegistrationModal';
import { SignInLayout } from 'Features/SignIn/SignInLayout';
import { addRedirectUrl, setInitialStep } from 'State/Integrations/IntegrationsActions';
import { hideOverlay, showOverlay } from 'State/Layouts/LayoutActions';
import { setSecondScreen } from 'State/SecondScreen/SecondScreenActions';
import { TRACKING_ENUMS, trackEvent } from 'Utils/Analytics';
import { ALT_CAPTCHA_ERROR_MESSAGE } from 'Utils/Constants';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { handleSignInSuccess, signInRedirect } from 'Utils/SignInHelpers';
import { getUser } from 'Utils/UserCookie';
import { getDefaultConfig } from 'Utils/Store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';

const INCORRECT_LOGIN_ERROR_MSG = 'Incorrect email address or password. Please try again.';
const EXCEED_LOGIN_ERROR_MSG = 'Maximum login attempts exceeded. Try again later...';
const LOGIN_OVERLAY_MSG = 'Signing in to your account...';

const cookies = new Cookies();

export function SignInContainer() {
  const dispatch = useDispatch();
  const locale = useSelector((s) => s.appConfig.strings);
  const defaultConfig = useSelector((s) => s.appConfig.defaultConfig);
  const routes = useSelector((s) => s.appConfig.routes);
  const { isGifting } = useSelector((s) => s.purchaseMode);
  const user = useSelector((s) => s.user);
  const { pathname = '' } = useLocation();
  const { signInCaptchaEnabled, signInCaptchaSiteKey } = defaultConfig;

  const [redirectOptions, setRedirectOptions] = useState({
    path: null,
    search: null,
    state: {},
  });
  const [showModal, setShowModal] = useState(false);
  const [deviceRegInfo, setDeviceRegInfo] = useState({});

  const isDeviceActivation = pathname.includes('tv');

  const onSignInSuccess = handleSignInSuccess();

  useEffect(() => {
    if (isDeviceActivation) {
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const device = urlParams.get('device');
      const secondScreen = {
        isDeviceActivation,
        code,
        device,
      };
      dispatch(setSecondScreen(secondScreen));
    }

    trackEvent(
      TRACKING_ENUMS.EVENT_LOGIN_STARTED,
      { setPageName: 'profile_sign_in_landing' },
      { dcg_profile_id: user.profileId }
    );

    const handleSignedInUser = async () => {
      const userData = getUser();
      if (!isDeviceActivation) dispatch(showOverlay(LOGIN_OVERLAY_MSG));

      const hasEntitlement = await onSignInSuccess(userData);

      const { isExternalRedirect, redirectPath, redirectSearch, redirectState, redirectStep, redirectUrl } =
        await signInRedirect(hasEntitlement, location);
      // if not external redirect, hide the overlay
      if (!isDeviceActivation && !isExternalRedirect) {
        dispatch(hideOverlay());
      }

      if (redirectStep) dispatch(setInitialStep(redirectStep));
      if (redirectUrl) dispatch(addRedirectUrl(redirectUrl));
      setRedirectOptions({
        path: redirectPath,
        search: redirectSearch,
        state: redirectState,
      });
    };

    if (user.isAuthenticated) {
      handleSignedInUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = async ({ email, password }) => {
    try {
      let recaptchaToken;
      if (signInCaptchaEnabled && window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(signInCaptchaSiteKey, { action: 'submit' });
      }
      const { deviceIdCookieName } = getDefaultConfig();
      const deviceId = cookies.get(deviceIdCookieName);
      const user = await login({ email, password, recaptchaToken, deviceId });
      if (!isDeviceActivation) dispatch(showOverlay(LOGIN_OVERLAY_MSG));
      const hasEntitlement = await onSignInSuccess(user);
      const { isExternalRedirect, redirectPath, redirectSearch, redirectState, redirectStep, redirectUrl } =
        await signInRedirect(hasEntitlement, location);
      // if not external redirect, hide the overlay
      if (!isDeviceActivation && !isExternalRedirect) {
        dispatch(hideOverlay());
      }

      if (redirectStep) dispatch(setInitialStep(redirectStep));
      if (redirectUrl) dispatch(addRedirectUrl(redirectUrl));
      setRedirectOptions({
        path: redirectPath,
        search: redirectSearch,
        state: redirectState,
      });
    } catch (error) {
      let message = '';
      const { statusCode, errorCode, ...errorData } = error;

      const errorType = statusCode >= 500 ? 'Server-side' : 'Other';
      const errorMsg = `${statusCode >= 500 ? 'HTTP 500 Error' : error.detail || error.message}: ${statusCode}`;

      message =
        errorCode === 429
          ? EXCEED_LOGIN_ERROR_MSG
          : error.message?.includes('reCaptcha')
          ? ALT_CAPTCHA_ERROR_MESSAGE
          : INCORRECT_LOGIN_ERROR_MSG;

      if (statusCode === 409) {
        const devices = Object.values(errorData);
        setDeviceRegInfo({
          login: { email, password },
          devices,
        });
        setShowModal(true);
        return;
      }

      trackEvent(
        TRACKING_ENUMS.EVENT_LOGIN_ERROR,
        {
          page_error_description: errorMsg,
          page_error_type: errorType,
        },
        {
          error_type: errorType,
          error_message: errorMsg,
        }
      );

      return onSubmitReactFormError(message || error);
    }
  };

  const closeDeviceModal = () => {
    setShowModal(false);
    setDeviceRegInfo(null);
    trackEvent(TRACKING_ENUMS.EVENT_BUTTON_LINK_SELECTED, {
      page_button_text: 'exit',
      page_name: 'account:limit',
      page_type: 'device limit',
    });
  };

  const handleDeviceModalSubmit = () => {
    setShowModal(false);
    // re-login after deleting one or more device sessions
    handleFormSubmit(deviceRegInfo.login);
  };

  if (redirectOptions.path) {
    return (
      <Navigate
        replace
        to={{
          pathname: redirectOptions.path,
          search: redirectOptions.search,
        }}
        state={redirectOptions.state}
      />
    );
  }

  return (
    <>
      <SignInLayout
        handleFormSubmit={handleFormSubmit}
        locale={locale}
        routes={routes}
        isGifting={isGifting}
        isDeviceActivation={isDeviceActivation}
        defaultConfig={defaultConfig}
      />
      {showModal && (
        <DeviceRegistrationModal
          onClose={closeDeviceModal}
          onSuccess={handleDeviceModalSubmit}
          devices={deviceRegInfo.devices}
          loginInfo={deviceRegInfo.login}
        />
      )}
    </>
  );
}
